import React, { useEffect, useState, useRef } from "react"
import { schedulers } from "../../../../apis"
import { Scheduler, SchedulerReport } from "../../../../types"
import { getSchedulerColumns } from "./schedulerLogColumns"
import TableNew from "../../../ui/table/TableNew"
import { KButton, KTitleSpan, KSpan } from "kahuna-base-react-components"

const POLLING_INTERVAL = 3000 // 3 seconds

const SchedulerLog = () => {
  const [loading, setLoading] = useState(false)
  const [schedulerList, setSchedulerList] = useState<Scheduler[]>([])
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<SchedulerReport | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [logContent, setLogContent] = useState<string | null>(null)
  const [reportStatus, setReportStatus] = useState<string | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setLoading(true)
    schedulers(`/latest_reports/`, "get").then((resp) => {
      setSchedulerList(resp.data.results)
      setLoading(false)
    })
  }, [])

  const fetchDetailedLogs = (reportId: number, isInitialFetch = false) => {
    if (isInitialFetch) {
      setDetailLoading(true)
      setLogContent(null)
      setReportStatus(null)
    }

    schedulers(`-report/${reportId}/detailed_logs/`, "get")
      .then((resp) => {
        setLogContent(resp.data.log_content)
        setReportStatus(resp.data.status)
        if (isInitialFetch) {
          setDetailLoading(false)
        }
      })
      .catch((err) => {
        console.error("Error fetching detailed logs:", err)
        if (isInitialFetch) {
          setLogContent(null)
          setReportStatus(null)
          setDetailLoading(false)
        }
      })
  }

  const handleReportClick = (report: SchedulerReport) => {
    setSelectedReport(report)
    setDetailModalOpen(true)
    fetchDetailedLogs(report.id, true)
  }

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }

  const handleCloseDetailModal = () => {
    stopPolling()
    setDetailModalOpen(false)
    setSelectedReport(null)
    setLogContent(null)
    setReportStatus(null)
  }

  // Polling effect - fetch logs every 3 seconds while job is in progress
  useEffect(() => {
    if (detailModalOpen && selectedReport && reportStatus === "in_progress") {
      pollingIntervalRef.current = setInterval(() => {
        fetchDetailedLogs(selectedReport.id, false)
      }, POLLING_INTERVAL)
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [detailModalOpen, selectedReport, reportStatus])

  const calculateTime = (endTime?: string, startTime?: string) => {
    if (!endTime || !startTime) {
      return "-"
    }
    const diffInMs = new Date(endTime).getTime() - new Date(startTime).getTime()
    const diffInMinutes = Math.floor(diffInMs / 60000)
    const hours = Math.floor(diffInMinutes / 60)
    const minutes = diffInMinutes % 60
    const seconds = Math.floor((diffInMs % 60000) / 1000)

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }

  const tableTitle = (
    <div className="flex flex-row gap-2 items-center !cursor-default">
      <div className="w-8 h-8 p-1.5 flex items-center justify-center">
        <img src="/admin_icons/desktop.svg" />
      </div>
      <div className="pt-0.5">
        {" "}
        <KTitleSpan text="Scheduler Logs" color="#111" fontSize={20} lineHeight="28px" />
      </div>
    </div>
  )

  const schedulerColumns = getSchedulerColumns(handleReportClick)

  return (
    <div className="catalog flex flex-grow justify-center">
      <div className="w-[100%]">
        <TableNew
          title={tableTitle}
          pagination
          minRowCount={15}
          limit={schedulerList.length}
          columns={schedulerColumns}
          data={schedulerList}
          count={schedulerList.length}
          rowHeightInPx={40}
          loading={loading}
        />
      </div>

      {/* Detail Modal */}
      {detailModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[90%] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <KTitleSpan
                text={`Scheduler Report Details - ID: ${selectedReport?.id}`}
                color="#111"
                fontSize={18}
                lineHeight="24px"
              />
              <button onClick={handleCloseDetailModal} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
                &times;
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto flex-grow">
              {detailLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <>
                  {selectedReport && (
                    <div className="mb-4 p-4 bg-gray-100 rounded">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <KSpan text={`Status: ${reportStatus || selectedReport.status}`} color="#111" fontWeight={500} />
                          {reportStatus === "in_progress" && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                              <span className="text-xs text-blue-500">Live</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <KSpan
                            text={`Start Date: ${selectedReport.date?.substring(0, 19).replace("T", " ")}`}
                            color="#111"
                            fontWeight={500}
                          />
                        </div>
                        <div>
                          <KSpan
                            text={`Finish Date: ${
                              selectedReport.finished_date?.substring(0, 19).replace("T", " ") || "-"
                            }`}
                            color="#111"
                            fontWeight={500}
                          />
                        </div>
                        <div>
                          <KSpan
                            text={`Duration: ${calculateTime(selectedReport.finished_date, selectedReport.date)}`}
                            color="#111"
                            fontWeight={500}
                          />
                        </div>
                        <div>
                          <KSpan text={`Read: ${selectedReport._read}`} color="#111" fontWeight={500} />
                        </div>
                        <div>
                          <KSpan text={`Insert: ${selectedReport._insert}`} color="#111" fontWeight={500} />
                        </div>
                        <div>
                          <KSpan text={`Update: ${selectedReport._update}`} color="#111" fontWeight={500} />
                        </div>
                        <div>
                          <KSpan text={`Delete: ${selectedReport._delete}`} color="#111" fontWeight={500} />
                        </div>
                        <div>
                          <KSpan text={`Errors: ${selectedReport._error}`} color="#111" fontWeight={500} />
                        </div>
                        <div>
                          <KSpan text={`Warnings: ${selectedReport._warning}`} color="#111" fontWeight={500} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <KSpan text="Detailed Log:" color="#111" fontWeight={600} fontSize={14} />
                    {logContent ? (
                      <pre
                        className="bg-gray-900 text-green-400 p-4 rounded overflow-auto mt-2"
                        style={{
                          maxHeight: "400px",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        {logContent}
                      </pre>
                    ) : (
                      <div className="text-gray-500 italic p-4 bg-gray-100 rounded mt-2">
                        No detailed logs available for this report.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-end">
              <KButton text="Close" onClick={handleCloseDetailModal} background="#111" textColor="white" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchedulerLog
